import React, { useState } from "react";
import { Link } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import Logo from "./partials/Svg";

function GuideWindow({ id, title, children }) {
  return (
    <section id={id} className="rg-window" aria-labelledby={`${id}-heading`}>
      <div className="rg-window-inner">
        {title && (
          <h2 id={`${id}-heading`} className="rg-window-title">
            {title}
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}

function MeterFigure({ image, alt, caption, onOpen }) {
  return (
    <figure className="rg-figure">
      <button
        type="button"
        className="rg-figure-btn"
        onClick={() => onOpen({ image, alt, caption })}
        aria-label={`Enlarge: ${alt}`}
      >
        <img src={image} alt={alt} loading="lazy" />
      </button>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function renderLinkedBody(item) {
  if (!item.body) return null;
  if (!item.linkTo) return <p>{item.body}</p>;

  const linkLabel = item.linkLabel || item.linkTo;
  const parts = item.body.split(linkLabel);
  if (parts.length === 1) {
    return (
      <p>
        {item.body}{" "}
        <a href={`#${item.linkTo}`}>{linkLabel}</a>
      </p>
    );
  }

  return (
    <p>
      {parts.map((part, i, arr) =>
        i < arr.length - 1 ? (
          <React.Fragment key={i}>
            {part}
            <a href={`#${item.linkTo}`}>{linkLabel}</a>
          </React.Fragment>
        ) : (
          part
        )
      )}
    </p>
  );
}

function HeroSection({ section, post }) {
  return (
    <GuideWindow id={section.id || "intro"} title={null}>
      <div className="rg-hero">
        <div className="rg-hero-logo" aria-hidden="true">
          <Logo />
        </div>
        <h1 className="rg-hero-title">{post.title || post.Caption}</h1>
        {(post.intro || section.intro) && (
          <p>{post.intro || section.intro}</p>
        )}
      </div>
    </GuideWindow>
  );
}

function TextSection({ section }) {
  return (
    <GuideWindow id={section.id} title={section.title}>
      <p>{section.body}</p>
    </GuideWindow>
  );
}

function SubtypeGridSection({ section, openLightbox }) {
  const gridClass =
    section.columns === 2
      ? "rg-subtype-grid rg-subtype-grid--two"
      : "rg-subtype-grid";

  return (
    <GuideWindow id={section.id} title={section.title}>
      {section.intro && <p>{section.intro}</p>}
      <div className={gridClass}>
        {(section.items || []).map((item) => (
          <article key={item.title} className="rg-subtype">
            <h3>{item.title}</h3>
            <MeterFigure
              image={item.image}
              alt={item.alt}
              onOpen={openLightbox}
            />
            {renderLinkedBody(item)}
          </article>
        ))}
      </div>
      {section.proTip && <p className="rg-pro-tip">{section.proTip}</p>}
    </GuideWindow>
  );
}

function InstructionSection({ section, openLightbox }) {
  return (
    <GuideWindow id={section.id} title={section.title}>
      {(section.paragraphs || []).map((p) => (
        <p key={p.slice(0, 40)}>{p}</p>
      ))}
      {(section.highlights || []).map((line) => (
        <p key={line.slice(0, 40)}>
          <strong>{line}</strong>
        </p>
      ))}
      {section.figure && (
        <MeterFigure
          image={section.figure.image}
          alt={section.figure.alt}
          onOpen={openLightbox}
        />
      )}
      {(section.subsections || []).map((sub) => (
        <React.Fragment key={sub.title}>
          <h3>{sub.title}</h3>
          {sub.kind === "steps" ? (
            <ol className="rg-steps">
              {(sub.steps || []).map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          ) : (
            <p>{sub.body}</p>
          )}
        </React.Fragment>
      ))}
    </GuideWindow>
  );
}

function ExamplesSection({ section, openLightbox }) {
  return (
    <GuideWindow id={section.id} title={section.title}>
      {section.intro && <p>{section.intro}</p>}
      <div className="rg-examples-grid">
        {(section.meters || []).map((meter, i) => (
          <MeterFigure
            key={meter.image}
            image={meter.image}
            alt={meter.alt}
            caption={`${i + 1}.`}
            onOpen={openLightbox}
          />
        ))}
      </div>
      {section.answerKeyTitle && (
        <details className="rg-answer-key">
          <summary>{section.answerKeyTitle}</summary>
          <ol>
            {(section.answers || []).map((ans) => (
              <li key={ans.value}>
                <strong>{ans.value}</strong>
                <p>{ans.note}</p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </GuideWindow>
  );
}

function BulletsSection({ section, openLightbox }) {
  return (
    <GuideWindow id={section.id} title={section.title}>
      {section.intro && <p>{section.intro}</p>}
      {section.figure && (
        <MeterFigure
          image={section.figure.image}
          alt={section.figure.alt}
          onOpen={openLightbox}
        />
      )}
      <ul className="rg-bullet-list">
        {(section.bullets || []).map((b) => (
          <li key={b.title}>
            <strong>{b.title}:</strong> {b.body}
          </li>
        ))}
      </ul>
    </GuideWindow>
  );
}

function CtaSection({ section }) {
  return (
    <GuideWindow id={section.id} title={section.title}>
      {(section.paragraphs || []).map((p) => (
        <p key={p.slice(0, 48)}>{p}</p>
      ))}
      {section.links?.length > 0 && (
        <p className="rg-cta">
          {section.links.map((link, i) => (
            <React.Fragment key={link.href}>
              {i > 0 && " · "}
              {link.kind === "route" ? (
                <Link to={link.href}>{link.label}</Link>
              ) : (
                <a href={link.href}>{link.label}</a>
              )}
            </React.Fragment>
          ))}
        </p>
      )}
    </GuideWindow>
  );
}

function renderSection(section, post, openLightbox) {
  switch (section.type) {
    case "hero":
      return (
        <HeroSection key={section.id} section={section} post={post} />
      );
    case "text":
      return <TextSection key={section.id} section={section} />;
    case "subtypeGrid":
      return (
        <SubtypeGridSection
          key={section.id}
          section={section}
          openLightbox={openLightbox}
        />
      );
    case "instruction":
      return (
        <InstructionSection
          key={section.id}
          section={section}
          openLightbox={openLightbox}
        />
      );
    case "examples":
      return (
        <ExamplesSection
          key={section.id}
          section={section}
          openLightbox={openLightbox}
        />
      );
    case "bullets":
      return (
        <BulletsSection
          key={section.id}
          section={section}
          openLightbox={openLightbox}
        />
      );
    case "cta":
      return <CtaSection key={section.id} section={section} />;
    default:
      return null;
  }
}

/** Fallback when a blog has openAsPage but no structured `sections`. */
function HtmlFallback({ post }) {
  return (
    <>
      <GuideWindow id="intro" title={null}>
        <div className="rg-hero">
          <div className="rg-hero-logo" aria-hidden="true">
            <Logo />
          </div>
          <h1 className="rg-hero-title">{post.title || post.Caption}</h1>
          {post.intro && <p>{post.intro}</p>}
        </div>
      </GuideWindow>
      <GuideWindow id="content" title={null}>
        <div
          className="rg-html-body"
          dangerouslySetInnerHTML={{ __html: post.Body || "" }}
        />
      </GuideWindow>
    </>
  );
}

/**
 * Guide-styled body for Blogs flagged with openAsPage.
 * Reuses reading-guide CSS; does not replace ReadingGuideBody.
 */
function GuideStylePostBody({ post }) {
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (item) => setLightbox(item);
  const closeLightbox = () => setLightbox(null);
 
  const nav = post.nav || [];
  const sections = post.sections || [];
  const hasSections = sections.length > 0;

  return (
    <div className="rg-page">
      <div className="rg-parallax" aria-hidden="true" />

      {nav.length > 0 && (
        <nav className="rg-nav" aria-label="Article sections">
          <ul>
            {nav.map((item) => (
              <li key={item.id}>
                <a href={`#${item.id}`}>{item.label}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div className="rg-content">
        {hasSections
          ? sections.map((section) =>
              renderSection(section, post, openLightbox)
            )
          : (
              <HtmlFallback post={post} />
            )}
      </div>

      <Modal
        show={!!lightbox}
        onHide={closeLightbox}
        size="xl"
        centered
        contentClassName="rg-lightbox-content"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h6 mb-0">
            {lightbox?.caption || lightbox?.alt}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center pt-2">
          {lightbox && (
            <img
              src={lightbox.image}
              alt={lightbox.alt}
              className="rg-lightbox-img"
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default GuideStylePostBody;
